import { createSlice, createSelector } from "@reduxjs/toolkit";
import { resolvePermission } from "../../utils/permissions";
import {
  deriveClinicRole,
  isAssignableTherapist,
} from "../../config/permissionsRegistry";
import { getEffectiveLimits } from "../../services/planLimits";
import { therapistApi } from "../services/therapistApi";

// sessionStorage key: a Hebrew login error stashed right before a forced
// sign-out (Auth account with no therapist profile). LoginPage reads and shows
// it on mount, because the sign-in briefly navigates away from /login.
export const PENDING_LOGIN_ERROR_KEY = "ck_pending_login_error";

const initialState = {
  user: null,
  therapistProfile: null,
  clinicProfile: null,
  isAuthenticated: false,
  isLoading: true,
  isNivAdmin: false, // Set from Firebase Custom Claims (not Firestore)
  error: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      // Note: Don't set isLoading to false here - wait until profiles are loaded
      state.error = null;
    },
    setTherapistProfile: (state, action) => {
      state.therapistProfile = action.payload;
    },
    addAcknowledgedSystemMessage: (state, action) => {
      if (!state.therapistProfile) return;
      const existing = state.therapistProfile.acknowledgedSystemMessages || [];
      if (!existing.includes(action.payload)) {
        state.therapistProfile.acknowledgedSystemMessages = [
          ...existing,
          action.payload,
        ];
      }
    },
    setClinicProfile: (state, action) => {
      state.clinicProfile = action.payload;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    },
    setNivAdmin: (state, action) => {
      state.isNivAdmin = action.payload === true;
    },
    clearError: (state) => {
      state.error = null;
    },
    logout: (state) => {
      state.user = null;
      state.therapistProfile = null;
      state.clinicProfile = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isNivAdmin = false;
      state.error = null;
    },
  },
});

export const {
  setUser,
  setTherapistProfile,
  addAcknowledgedSystemMessage,
  setClinicProfile,
  setLoading,
  setError,
  clearError,
  setNivAdmin,
  logout,
} = authSlice.actions;

export const selectUser = (state) => state.auth.user;
export const selectTherapistProfile = (state) => state.auth.therapistProfile;
export const selectClinicProfile = (state) => state.auth.clinicProfile;
export const selectClinicId = (state) => state.auth.therapistProfile?.clinicId;
export const selectTherapistId = (state) => state.auth.therapistProfile?.uid;
export const selectTherapistName = (state) => {
  const profile = state.auth.therapistProfile;
  if (!profile) return null;
  return `${profile.firstName || ""} ${profile.lastName || ""}`.trim() || null;
};
// 'male' | 'female' | 'other' | null — used for gendered Hebrew copy.
export const selectTherapistGender = (state) =>
  state.auth.therapistProfile?.gender || null;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsLoading = (state) => state.auth.isLoading;
export const selectAuthError = (state) => state.auth.error;
export const selectIsAdmin = (state) =>
  state.auth.therapistProfile?.isAdmin === true ||
  state.auth.therapistProfile?.role === "owner";

// ============================================
// THERAPIST PERMISSIONS SELECTORS (Permissions v2)
// ============================================
// Single resolver: src/utils/permissions.js `resolvePermission(key, t, c)`.
//   admin bypass → therapist.permissions[key] → clinic.therapistPermissions[key]
//   → registry legacyDefault (== pre-v2 behavior for existing clinics).
// Never read the raw flags directly; use these selectors.
// ============================================

const hasPermSelectorCache = new Map();

/**
 * Selector factory: `useSelector(selectHasPermission("calendar.viewClinic"))`.
 * Memoized per key so the same selector reference is returned each render.
 */
export const selectHasPermission = (key) => {
  let sel = hasPermSelectorCache.get(key);
  if (!sel) {
    sel = (state) =>
      resolvePermission(key, state.auth.therapistProfile, state.auth.clinicProfile);
    hasPermSelectorCache.set(key, sel);
  }
  return sel;
};

/**
 * Can the current user access finance features?
 * Controls: Finance page, payment amounts, pending payment steps, patient rates
 */
export const selectCanAccessFinance = selectHasPermission("allowFinanceAccess");

/**
 * Can the current user create invoices (unified for Morning + Digital Invoice)?
 * Resolver keeps the legacy per-integration allowTherapistsToCreateInvoices
 * fallback when the unified flag is absent.
 */
export const selectCanCreateInvoices = selectHasPermission("allowCreateInvoices");

/**
 * Can the current user export data (CSV, reports)?
 */
export const selectCanExportData = selectHasPermission("allowExportData");

/**
 * Clinic scheduling policies (clinic.multiClinicSettings, Phase 4).
 * Absent ⇒ false ⇒ warn-only (existing clinics never get blocked).
 * Solo clinics: a therapist can still overlap themself — same warn/block
 * semantics apply, but the settings card is hidden so they stay warn-only.
 */
export const selectPreventTherapistDoubleBooking = selectHasPermission(
  "scheduling.preventTherapistDoubleBooking",
);
export const selectBlockBookingOnAbsence = selectHasPermission(
  "scheduling.blockBookingOnAbsence",
);

/**
 * Patient sharing mode (Phase 3): "assigned" (today's model) | "all" (every
 * active therapist sees/edits every patient). Absent ⇒ "assigned".
 */
export const selectPatientSharingMode = selectHasPermission("patientSharing.mode");
export const selectClinicSharesAllPatients = (state) =>
  !state.auth.clinicProfile?.isSoloClinic &&
  selectPatientSharingMode(state) === "all";
/** In "all" mode: are treatment reports shared too? (default true) */
export const selectClinicSharesTreatments = (state) =>
  selectClinicSharesAllPatients(state) &&
  selectHasPermission("patientSharing.allIncludesTreatments")(state) === true;

/** "owner" | "manager" | "therapist" | "secretary" (derived for legacy docs). */
export const selectClinicRole = (state) =>
  deriveClinicRole(state.auth.therapistProfile);

/** Secretary: no own patients / calendar / treatment reports. */
export const selectIsSecretary = (state) =>
  selectClinicRole(state) === "secretary";

const EMPTY_LIST = [];
const assignableSelectorCache = new Map();

/**
 * Active, non-secretary therapists of the current clinic — the ONLY list to
 * offer in ownership dropdowns (patient / appointment therapist pickers,
 * reassignment and transfer targets, report scope, calendar legends).
 * Reads the RTK cache of `getClinicTherapists`; empty until it is loaded.
 */
export const selectAssignableTherapists = (state) => {
  const clinicId = selectClinicId(state);
  if (!clinicId) return EMPTY_LIST;
  let sel = assignableSelectorCache.get(clinicId);
  if (!sel) {
    sel = createSelector(
      [therapistApi.endpoints.getClinicTherapists.select({ clinicId })],
      (result) => {
        const list = result?.data;
        if (!Array.isArray(list)) return EMPTY_LIST;
        return list.filter(isAssignableTherapist);
      },
    );
    assignableSelectorCache.set(clinicId, sel);
  }
  return sel(state);
};

/**
 * Is multi-therapist patient assignment enabled for this clinic?
 * Only available for non-solo clinics with the toggle enabled.
 */
export const selectAllowMultipleTherapists = (state) => {
  const { clinicProfile } = state.auth;
  if (clinicProfile?.isSoloClinic) return false;
  return clinicProfile?.therapistPermissions?.allowMultipleTherapists === true;
};

// ============================================
// PUNCH CARDS (כרטיסיות) SELECTORS
// ============================================

/**
 * Has this clinic enabled the punch cards (כרטיסיות) feature?
 * Opt-in: absent on existing clinic docs → disabled (zero behavior change).
 */
export const selectPunchCardsEnabled = (state) =>
  state.auth.clinicProfile?.punchCardsEnabled === true;

/**
 * Has this clinic enabled online video meetings (100ms)?
 * Opt-in: absent on existing clinic docs → disabled (zero behavior change).
 */
export const selectOnlineMeetingsEnabled = (state) =>
  state.auth.clinicProfile?.onlineMeetingsEnabled === true;

/**
 * Does this clinic manage couples as patients (single patient doc per couple)?
 * Opt-in: absent on existing clinic docs → disabled (zero behavior change).
 *
 * Never true for a nutrition clinic — couple intake is not a thing for a
 * nutritionist, so every "create a couple" surface hides there. Existing
 * couple patients still render: readers check `patient.isCouple` directly,
 * not this selector.
 */
export const selectClinicManagesCouplesAsPatients = (state) =>
  state.auth.clinicProfile?.clinicManagesCouplesAsPatients === true &&
  !selectIsNutritionClinic(state);

/**
 * Does this clinic accept temp customers (לקוחות מזדמנים) — one-off clients
 * scheduled without opening a patient record?
 * Opt-in: absent on existing clinic docs → disabled (zero behavior change).
 */
export const selectTempCustomersEnabled = (state) =>
  state.auth.clinicProfile?.tempCustomersEnabled === true;

/**
 * Does this clinic treat English-speaking patients? When on, each patient can
 * be marked `communicationLanguage: "en"`, which switches their SMS and their
 * appointment-confirmation page to English.
 * Opt-in: absent on existing clinic docs → disabled (zero behavior change).
 */
export const selectEnglishCommunicationEnabled = (state) =>
  state.auth.clinicProfile?.englishCommunicationEnabled === true;

/**
 * Clinic-level end-meeting (post-session) SMS settings map, or null if unset.
 */
export const selectEndMeetingSmsSettings = (state) =>
  state.auth.clinicProfile?.endMeetingSms || null;

/**
 * Has this clinic enabled the end-meeting SMS feature?
 * Opt-in: absent on existing clinic docs → disabled (zero behavior change).
 */
export const selectIsEndMeetingSmsEnabled = (state) =>
  state.auth.clinicProfile?.endMeetingSms?.enabled === true;

// ============================================
// NUTRITION (תזונה ודיאטה) SELECTORS
// ============================================

/**
 * Is this a nutrition / dietitian clinic?
 * Drives the nutrition module: menu builder, measurements, daily eating
 * reports, and the public client page — instead of the treatment-report flow.
 *
 * Opt-in by clinic type (chosen at signup). `nutritionFeaturesEnabled` is an
 * optional NiV kill switch: absent on every clinic doc → module stays on for
 * nutrition clinics, and off for everyone else (zero behavior change).
 */
export const selectIsNutritionClinic = (state) =>
  state.auth.clinicProfile?.type === "nutrition" &&
  state.auth.clinicProfile?.nutritionFeaturesEnabled !== false;

// ============================================
// MORNING / DIGITAL INVOICE PERMISSION SELECTORS
// ============================================

/**
 * Check if the current user can create invoices in Morning.
 * Uses the unified allowCreateInvoices permission.
 */
export const selectCanCreateMorningInvoice = (state) => {
  const clinicProfile = state.auth.clinicProfile;
  if (!clinicProfile?.isMorningPaymentSystemIntegrated) return false;
  return selectCanCreateInvoices(state);
};

/**
 * Selector: Can the current user create Digital Invoice invoices?
 * Uses the unified allowCreateInvoices permission.
 */
/**
 * Grow payments (grow_integration.md §7).
 * selectIsGrowIntegrated — clinic connected (server-written flag).
 * selectPlanAllowsGrow   — plan flag `canIntegrateGrow` (incl. NiV planOverrides).
 * selectCanCollectViaGrow — permission key finance.collectViaGrow (admin bypass,
 *                           requires allowFinanceAccess via the registry).
 * selectCanUseGrow       — all three: what gates every "request payment" button.
 * selectCanConnectGrow   — settings card: admin + plan flag (connection itself
 *                           is admin-only; integrated or not).
 */
export const selectIsGrowIntegrated = (state) =>
  state.auth.clinicProfile?.isGrowIntegrated === true;

export const selectPlanAllowsGrow = (state) => {
  const clinic = state.auth.clinicProfile;
  if (!clinic) return false;
  return getEffectiveLimits(clinic.plan, clinic).canIntegrateGrow === true;
};

export const selectCanCollectViaGrow = (state) =>
  selectCanAccessFinance(state) && selectHasPermission("finance.collectViaGrow")(state);

export const selectCanUseGrow = (state) =>
  selectIsGrowIntegrated(state) && selectPlanAllowsGrow(state) && selectCanCollectViaGrow(state);

export const selectCanConnectGrow = (state) => selectIsAdmin(state) && selectPlanAllowsGrow(state);

/** Leads v2 (leads_plan.md §4.4): plan flag `canUseLeadsWebhook` incl. NiV planOverrides (default true everywhere). */
export const selectPlanAllowsLeadsWebhook = (state) => {
  const clinic = state.auth.clinicProfile;
  if (!clinic) return false;
  return getEffectiveLimits(clinic.plan, clinic).canUseLeadsWebhook !== false;
};

export const selectGrowIntegration = (state) => state.auth.clinicProfile?.growIntegration || null;

export const selectCanCreateDigitalInvoice = (state) => {
  const clinicProfile = state.auth.clinicProfile;
  if (!clinicProfile?.isDigitalInvoiceIntegrated) return false;
  return selectCanCreateInvoices(state);
};

/**
 * Unified selector: Is ANY green invoice system connected (Morning OR Digital Invoice)?
 */
export const selectIsUsingGreenInvoice = (state) => {
  const clinicProfile = state.auth.clinicProfile;
  return (
    clinicProfile?.isMorningPaymentSystemIntegrated === true ||
    clinicProfile?.isDigitalInvoiceIntegrated === true
  );
};

/**
 * Unified selector: Can the current user create invoices (via Morning OR Digital Invoice)?
 */
export const selectCanCreateGreenInvoice = (state) => {
  return selectCanCreateMorningInvoice(state) || selectCanCreateDigitalInvoice(state);
};

/**
 * Selector: Is Digital Invoice the active provider?
 * Used to determine which modal to show.
 */
export const selectIsDigitalInvoiceActive = (state) => {
  return state.auth.clinicProfile?.isDigitalInvoiceIntegrated === true;
};

// NiV User selector - for SaaS admin dashboard access
// Note: Field in Firestore is "isNivUser" (lowercase v)
export const selectIsNiVUser = (state) =>
  state.auth.therapistProfile?.isNivUser === true;

// NiV Admin selector - verified via Firebase Custom Claims (secure)
// This is the authoritative check for admin access
export const selectIsNivAdmin = (state) => state.auth.isNivAdmin === true;

// Check if user profile is fully loaded (has therapist profile data)
export const selectIsProfileLoaded = (state) =>
  state.auth.therapistProfile !== null;

// Whole days since this therapist registered. null when the profile has not
// loaded yet or carries no registrationDate, so callers can tell "unknown"
// apart from "registered today".
export const selectDaysSinceRegistration = (state) => {
  const registrationDate =
    state.auth.therapistProfile?.registrationDate ||
    state.auth.therapistProfile?.createdAt;
  if (!registrationDate) return null;
  // Normally an ISO string (serializeForRedux), but tolerate a raw/serialized
  // Firestore Timestamp so the gate never silently reads as "unknown".
  const registered =
    typeof registrationDate.toDate === "function"
      ? registrationDate.toDate()
      : typeof registrationDate.seconds === "number"
        ? new Date(registrationDate.seconds * 1000)
        : new Date(registrationDate);
  if (Number.isNaN(registered.getTime())) return null;
  return Math.floor((Date.now() - registered.getTime()) / (1000 * 60 * 60 * 24));
};

// Clinic deactivated by system admin (NiV) — blocks the entire dashboard
export const selectIsClinicInactiveByAdmin = (state) =>
  state.auth.clinicProfile?.inactiveByAdmin === true;

// ============================================
// TRIAL STATUS SELECTORS
// ============================================

// Check if clinic is on trial plan
export const selectIsTrialPlan = (state) =>
  state.auth.clinicProfile?.plan === "trial";

// Get trial end date as Date object
export const selectTrialEndDate = (state) => {
  const trialEndDate = state.auth.clinicProfile?.trialEndDate;
  if (!trialEndDate) return null;
  // Handle both Timestamp and ISO string formats
  if (typeof trialEndDate === "string") {
    return new Date(trialEndDate);
  }
  if (trialEndDate.toDate) {
    return trialEndDate.toDate();
  }
  return new Date(trialEndDate);
};

// Check if trial has expired
export const selectIsTrialExpired = (state) => {
  const isTrialPlan = selectIsTrialPlan(state);
  if (!isTrialPlan) return false;

  const trialEndDate = selectTrialEndDate(state);
  if (!trialEndDate) return false;

  const now = new Date();
  return now > trialEndDate;
};

// Get days remaining in trial (negative if expired)
export const selectTrialDaysRemaining = (state) => {
  const isTrialPlan = selectIsTrialPlan(state);
  if (!isTrialPlan) return null;

  const trialEndDate = selectTrialEndDate(state);
  if (!trialEndDate) return null;

  const now = new Date();
  const diffTime = trialEndDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Check if we should show trial warning (3, 2, or 1 days before expiration)
export const selectShouldShowTrialWarning = (state) => {
  const daysRemaining = selectTrialDaysRemaining(state);
  if (daysRemaining === null) return false;
  return daysRemaining > 0 && daysRemaining <= 3;
};

// ============================================
// SUBSCRIPTION (PAID PLAN) STATUS SELECTORS
// ============================================
//
// `subscriptionEndDate` reflects how long a paid plan is valid for. (It was
// previously set by the Grow payment webhook, which has been removed now that
// billing is handled manually.) When it lapses, we want the contact popup to
// show again — even though the `plan` field itself still says
// "premium" / "starter" / etc.
//
// Trial plan does NOT use these selectors — its lifecycle is governed by
// `trialEndDate` via the Trial selectors above.
// ============================================

const PLAN_CODES_REQUIRING_PAID_SUBSCRIPTION = ["starter", "starter-plus", "premium"];

export const selectSubscriptionEndDate = (state) => {
  const raw = state.auth.clinicProfile?.subscriptionEndDate;
  if (!raw) return null;
  if (typeof raw === "string") return new Date(raw);
  if (raw.toDate) return raw.toDate();
  return new Date(raw);
};

/**
 * True when the clinic's paid subscription has lapsed (paid plan whose
 * subscriptionEndDate is in the past). Trial plans return false here —
 * use selectIsTrialExpired for them.
 */
export const selectIsSubscriptionExpired = (state) => {
  const plan = state.auth.clinicProfile?.plan;
  if (!plan || !PLAN_CODES_REQUIRING_PAID_SUBSCRIPTION.includes(plan)) {
    return false;
  }
  const end = selectSubscriptionEndDate(state);
  // No end date set yet (e.g. legacy clinic without webhook) → don't block.
  if (!end) return false;
  return new Date() > end;
};

/**
 * Unified selector — true when the blocking paywall modal should be shown,
 * because either the trial lapsed or the paid subscription lapsed.
 */
export const selectShouldShowPaywall = (state) => {
  return selectIsTrialExpired(state) || selectIsSubscriptionExpired(state);
};

export default authSlice.reducer;
